import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import { cn } from "../lib/utils"
import { useResolveCls } from "../contexts/breakpoint-context"
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion"
import type { CogoElement } from "../core/types"
import { useBoundValue } from "../hooks/use-bound-value"
import { readResolvedArray, recordValue, resolvedText, shorthandId } from "./property-value-utils"
import { resolveClassNameDesignAuthority } from "../lib/design-handle-resolver"

function resolvedOpenItems(
  element: CogoElement,
  resolveSymbol: (value: string) => unknown,
): string[] {
  const rawOpenItems = (element as CogoElement & { openItems?: unknown }).openItems
  const authoredValue = rawOpenItems !== undefined
    ? rawOpenItems
    : element.defaultValue
  const resolvedValue = typeof authoredValue === "string"
    ? resolveSymbol(authoredValue)
    : authoredValue

  if (Array.isArray(resolvedValue)) {
    return resolvedValue.map(String)
  }
  return resolvedValue == null ? [] : [String(resolvedValue)]
}

export function AccordionRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const rcls = useResolveCls()
  const resolveChildClassName = (child: CogoElement | undefined): string | undefined => {
    if (!child?.className) return child?.className
    const resolution = resolveClassNameDesignAuthority(
      child.className,
      resolveSymbol,
      child.classNameVariants,
      child.variantKey,
    )
    if (resolution.unresolvedHandles.length > 0) {
      console.warn("[cogo] unresolved accordion child design-system handles", resolution.unresolvedHandles)
    }
    return rcls(resolution.className)
  }
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("accordion")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const [openItems, setOpenItems] = useBoundValue<string[]>(
    element.bind,
    resolveSymbol,
    resolvedOpenItems(element, resolveSymbol),
    (raw) => Array.isArray(raw)
      ? raw.map(String)
      : raw == null
        ? []
        : [String(raw)],
  )

  const handleValueChange = (value: string | string[]) => {
    const nextOpenItems = Array.isArray(value) ? value.map(String) : [String(value)]
    setOpenItems(nextOpenItems)
    dispatchEvent(element.id, "toggle", {
      value: nextOpenItems,
      openItems: nextOpenItems,
    })
  }

  const explicitChildren = element.children ?? []
  const shorthandItems = explicitChildren.length === 0
    ? readResolvedArray(element, "items", resolveSymbol).map((raw, index) => {
        const item = recordValue(raw)
        const id = String(item.id ?? shorthandId(element.id, "items", index))
        const content = item.content
        const contentChildren: CogoElement[] = Array.isArray(content)
          ? content.map((entry, childIndex) => {
              const record = recordValue(entry)
              return {
                ...record,
                id: String(record.id ?? shorthandId(id, "content", childIndex)),
                type: String(record.type ?? "text"),
                text: record.text ?? record.label,
              } as unknown as CogoElement
            })
          : [{ id: `${id}__content_text`, type: "text", text: resolvedText(content, resolveSymbol) }]
        return {
          ...item,
          id,
          type: "accordion-item",
          value: item.value ?? id,
          disabled: item.disabled,
          children: [
            { id: `${id}__trigger`, type: "accordion-trigger", text: resolvedText(item.title ?? item.label, resolveSymbol, `Section ${index + 1}`) },
            { id: `${id}__content`, type: "accordion-content", children: contentChildren },
          ],
        } as unknown as CogoElement
      })
    : []
  // JITRULE Task 2: authored (element.children) sections skip the
  // recordValue()-based shorthand-items path above, so they need their own
  // liftV4Props before any nested field (title/text/etc.) is read below.
  const authoredSections = (explicitChildren.length > 0 ? explicitChildren : shorthandItems)
    .map(liftV4Props)

  return (
    <Accordion
      multiple
      value={openItems}
      className={cn("w-full", element.className)}
      data-testid={`accordion-${element.id}`}
      data-atom-type="accordion"
      data-open-items={JSON.stringify(openItems)}
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      onValueChange={handleValueChange}
    >
      {authoredSections.map((section, index) => {
        // Support both flat items (legacy) and nested accordion-item structure (v2)
        const isAccordionItem = section.type === "accordion-item"

        // v2: accordion-item may contain accordion-trigger + accordion-content children.
        // These are one level deeper than `section` (section.children), so
        // section's own liftV4Props above does not cover them — lift again.
        const triggerChild = isAccordionItem
          ? (() => {
              const raw = section.children?.find(c => c.type === "accordion-trigger")
              return raw ? liftV4Props(raw) : undefined
            })()
          : undefined
        const contentChild = isAccordionItem
          ? (() => {
              const raw = section.children?.find(c => c.type === "accordion-content")
              return raw ? liftV4Props(raw) : undefined
            })()
          : undefined

        const title = triggerChild?.text
          ? String(resolveSymbol(triggerChild.text) ?? "")
          : section.text
          ? String(resolveSymbol(section.text) ?? "")
          : `Section ${index + 1}`

        const contentChildren = contentChild?.children ?? section.children ?? []

        return (
          <AccordionItem
            key={section.id}
            value={String(section.value ?? section.id)}
            disabled={section.disabled === true}
            className={cn(resolveChildClassName(section))}
            data-cogo-element-id={section.id}
            data-cogo-type={section.type}
          >
            <AccordionTrigger
              className={cn(resolveChildClassName(triggerChild))}
              data-cogo-element-id={triggerChild?.id}
              data-cogo-type={triggerChild?.type}
            >
              {title}
            </AccordionTrigger>
            <AccordionContent
              className={cn(resolveChildClassName(contentChild))}
              data-cogo-element-id={contentChild?.id}
              data-cogo-type={contentChild?.type}
            >
              {contentChildren.map((child) => (
                <ElementRenderer key={child.id} element={child} />
              ))}
            </AccordionContent>
          </AccordionItem>
        )
      })}
    </Accordion>
  )
}
